<template>
  <LoadingSpinner v-if="projectLoading || customerLoading || budgetLoading || contractTermsLoading || !proposalInitalized" />

  <div v-else class="create-proposal">
    <ProjectHeader :project="project" />
    <ProjectSubNavigation :project="project" />

    <div class="mx-auto max-w-7xl card">
      <ProposalForm
        :project="project"
        :customer="customer"
        :budget="budget"
        :model="proposal"
        :auto-save="enableAutoSave"
        :auto-save-interval="autoSaveInterval"
        :save-function="saveProposal"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { Proposal, generateContractId } from '@/models/proposal'
import { map } from 'underscore'
import { Budget } from '@/models/budget'
import ProposalForm from '../components/ProposalForm.vue'
import ProjectSubNavigation from '@/components/project/ProjectSubNavigation.vue'
import ProjectHeader from '@/components/project/ProjectHeader.vue'
import { useHead } from '@unhead/vue'
import { useNotification } from '@kyvg/vue3-notification'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { useRoute, useRouter } from 'vue-router'
import BudgetService from '@/services/BudgetService'
import ProjectService from '@/services/ProjectService'
import { computed, ref, watch } from 'vue'
import CustomerService from '@/services/CustomerService'
import ProposalService from '@/services/ProposalService'
import { useAuth } from '@/composables/useAuth'
import SettingService from '@/services/SettingService'

useHead({ title: 'Create new proposal' })


const { notify } = useNotification()
const $router = useRouter()
const $route = useRoute()

const budgetService = new BudgetService()
const projectService = new ProjectService()
const customerService = new CustomerService()
const proposalService = new ProposalService()
const { currentUser } = useAuth()
const queryClient = useQueryClient()

const proposal = ref(new Proposal())
const projectId = $route.params.id as string || ''
const enableAutoSave = ref(true)
const autoSaveInterval = ref(10)

const { data: project, isLoading: projectLoading } = useQuery({
  queryKey: ['project', projectId],
  queryFn: async () => {
    const project = await projectService.find(projectId)
    return project
  },
})

const { data: budget, isLoading: budgetLoading } = useQuery({
  queryKey: ['budget', projectId],
  queryFn: async () =>  {
    const budget = await budgetService.find(projectId)
    const lineItems = await budgetService.getLineItems(projectId)
    budget.lineItems = lineItems
    return budget
  },
})

const enableQuery = computed(() => project.value?.customerId !== '')

const { data: customer, isLoading: customerLoading } = useQuery({
  queryKey: ['customer', project.value?.customerId],
  queryFn: () => customerService.find(project.value?.customerId),
  enabled: enableQuery
})

const proposalInitalized = ref(false)

const { data:generalSettings, isLoading: contractTermsLoading } = useQuery({
  queryKey: ['settings', 'generalSettings'],
  queryFn: async () => {
    try {
      return await (new SettingService().find('generalSettings'))
    } catch (_) {
      notify({
        type: 'error',
        title: 'Error',
        text: 'Failed to get contract terms'
      })
      return {}
    }
  }
})

const isLoading = computed(() => projectLoading.value || budgetLoading.value || customerLoading.value || contractTermsLoading.value)

watch(() => isLoading.value, async (loading) => {
  if (!loading && project.value && budget.value && customer.value) {
    proposal.value.projectId = project.value.id
    proposal.value.budgetId = project.value.id
    proposal.value.preparerId = currentUser.value.id
    proposal.value.customerId = project.value.customerId
    proposal.value.contractId = await generateContractId()
    const validDate = proposal.value.contractDate.getMonth() + 1
    proposal.value.contractValidDate.setMonth(validDate)
    proposal.value.lineItems = map(budget.value.lineItems, lineItem => lineItem.id)
    proposal.value.id = project.value.id
    proposal.value.termsConditions = generalSettings.value?.contractTerms ?? ''

    enableAutoSave.value = Object.hasOwn(generalSettings.value, 'enableProposalAutoSave') ? generalSettings.value.enableProposalAutoSave : true
    autoSaveInterval.value = generalSettings.value?.autoSaveWaitTime ?? 10
    proposalInitalized.value = true
  }
}, { immediate: true })


const { mutateAsync: saveProposal } = useMutation({
  mutationFn: async (payload: {proposal: Proposal, budget: Budget}) => {
    const budget = await budgetService.updateBudget(payload.budget, [])
    const proposal = await proposalService.update(payload.proposal.projectId, payload.proposal)
    return { budget, proposal }
  },
  onSuccess: (payload: { budget: Budget, proposal: Proposal }) => {
    notify({
      type: 'success',
      title: 'Proposal Created',
      text: 'Proposal created successfully'
    })
    queryClient.setQueryData(['proposal', projectId], payload.proposal)
    queryClient.setQueryData(['budget', projectId], payload.budget)
    $router.push({ name: 'ShowProposal', params: { id: project.value.id } })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error updating proposal',
      text: error.message || 'An error occurred while updating the proposal'
    })
  }
})

</script>
